import type {
  AnnotationDocument,
  ModelCoordinateMetadata,
  Project,
  SpatialCompatibility,
} from "@factory-twin/contracts";
import { fileMd5 } from "./file-checksum";

interface ApiErrorBody {
  error?: { message?: string };
}

export type ModelAssetQuality = "standard" | "low";

export interface ModelVariantSelection {
  requested: ModelAssetQuality;
  served: ModelAssetQuality;
  fallback: boolean;
}

export interface SceneSummary {
  id: string;
  projectId: string;
  status: "PENDING" | "PROCESSING" | "COMPLETED" | "FAILED";
  errorMessage: string | null;
  createdAt: string;
  hasModel: boolean;
  fileSize: number | null;
  modelVersion: string | null;
  coordinateFrameId: string | null;
  coordinateConvention: "RDF";
  alignmentStatus: ModelCoordinateMetadata["alignmentStatus"];
  alignmentRmse: number | null;
}

export interface AnnotationResponse {
  document: AnnotationDocument;
  compatibility: SpatialCompatibility;
  updatedAt: string | null;
}

export interface OperationsSummary {
  windowDays: number;
  slo: Array<{ name: string; objective: number; value: number | null; denominator: number; lowerIsBetter?: boolean; averageDurationSeconds?: number | null }>;
  cost: { usd: number; gpuSeconds: number; budgetUsd: number };
  alerts: Array<{ id: string; severity: string; alert_type: string; message: string; last_detected_at: string; correlation_id: string | null }>;
  jobs: Array<{ id: string; scene_id: string; correlation_id: string; provider: string; state: string; attempt_count: number; last_error: string | null; cost_usd: number; updated_at: string }>;
}

export class ApiClient {
  constructor(
    private readonly getToken: () => string | null,
    private readonly baseUrl = import.meta.env.VITE_API_BASE_URL?.replace(/\/$/u, "") ?? "",
  ) {}

  resolveUrl(path: string): string {
    return path.startsWith("/api/") ? `${this.baseUrl}${path}` : path;
  }

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const token = this.getToken();
    const response = await fetch(this.resolveUrl(path), {
      ...init,
      headers: {
        ...(init.body instanceof Blob ? {} : { "Content-Type": "application/json" }),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...init.headers,
      },
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({})) as ApiErrorBody;
      throw new Error(body.error?.message ?? `Request failed (${response.status})`);
    }
    if (response.status === 204) return undefined as T;
    return response.json() as Promise<T>;
  }

  authenticate(mode: "login" | "register", email: string, password: string) {
    return this.request<{ token: string; user: { id: string; email: string } }>(`/api/auth/${mode}`, {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
  }

  listProjects() {
    return this.request<{ projects: Project[] }>("/api/projects");
  }

  createProject(name: string, description: string) {
    return this.request<{ project: Project }>("/api/projects", {
      method: "POST",
      body: JSON.stringify({ name, description }),
    });
  }

  listScenes(projectId: string) {
    return this.request<{ scenes: SceneSummary[] }>(`/api/projects/${projectId}/scenes`);
  }

  async uploadVideo(projectId: string, file: File, onProgress: (value: number) => void, sceneId?: string) {
    const contentType = file.type || "video/mp4";
    const checksum = await fileMd5(file);
    const metadata = await this.request<{
      videoId: string;
      uploadUrl: string;
      requiredHeaders: Record<string, string>;
    }>(`/api/projects/${projectId}/uploads`, {
      method: "POST",
      body: JSON.stringify({ fileName: file.name, contentType, fileSize: file.size, checksum, ...(sceneId ? { sceneId } : {}) }),
    });
    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", this.resolveUrl(metadata.uploadUrl));
      for (const [name, value] of Object.entries(metadata.requiredHeaders)) xhr.setRequestHeader(name, value);
      if (metadata.uploadUrl.startsWith("/api/")) {
        const token = this.getToken();
        if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      }
      xhr.upload.onprogress = (event) => event.lengthComputable && onProgress(Math.round(event.loaded / event.total * 100));
      xhr.onload = () => xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed (${xhr.status})`));
      xhr.onerror = () => reject(new Error("Upload connection failed"));
      xhr.send(file);
    });
    onProgress(100);
    return this.request<{ sceneId: string; status: string }>(`/api/uploads/${metadata.videoId}/complete`, {
      method: "POST",
      body: "{}",
    });
  }

  getModelUrl(sceneId: string, quality: ModelAssetQuality = "standard") {
    const query = quality === "low" ? "?quality=low" : "";
    return this.request<{ url: string; expiresIn: number; variant: ModelVariantSelection } & ModelCoordinateMetadata>(`/api/scenes/${sceneId}/model${query}`);
  }

  getAnnotations(sceneId: string) {
    return this.request<AnnotationResponse>(`/api/scenes/${sceneId}/annotations`);
  }

  saveAnnotations(document: AnnotationDocument) {
    return this.request<AnnotationResponse>(`/api/scenes/${document.sceneId}/annotations`, {
      method: "PUT",
      body: JSON.stringify(document),
    });
  }

  reanchorAnnotations(sceneId: string, action: "APPLY_TRANSFORM" | "DISCARD") {
    return this.request<AnnotationResponse>(`/api/scenes/${sceneId}/annotations/reanchor`, {
      method: "POST",
      body: JSON.stringify({ action }),
    });
  }

  operationsSummary() { return this.request<OperationsSummary>("/api/operations/summary"); }
  operationJob(jobId: string) { return this.request<{ job: Record<string, unknown>; attempts: Array<Record<string, unknown>>; logs: Array<{ id: string; event_type: string; payload: string; created_at: string }> }>(`/api/operations/jobs/${jobId}`); }
  retryJob(jobId: string) { return this.request<{ accepted: boolean }>(`/api/operations/jobs/${jobId}/retry`, { method: "POST", body: "{}" }); }
  cancelJob(jobId: string) { return this.request<{ accepted: boolean }>(`/api/operations/jobs/${jobId}/cancel`, { method: "POST", body: "{}" }); }
}
